import { i18n } from "@lingui/core";

export type SupportedLocale = "en-US" | "ko-KR" | "ja-JP";

export const DEFAULT_LOCALE: SupportedLocale = "en-US";

const SUPPORTED_LOCALES: SupportedLocale[] = ["en-US", "ko-KR", "ja-JP"];

export function detectLocale(configLocale?: string): SupportedLocale {
  // Config default locale (fallback)
  const defaultLocale =
    configLocale && isValidLocale(configLocale)
      ? (configLocale as SupportedLocale)
      : DEFAULT_LOCALE;

  // Priority 1: Check URL parameter (highest priority)
  // Support case-insensitive matching
  if (typeof window !== "undefined") {
    const urlParams = new URLSearchParams(window.location.search);
    const urlLocale = urlParams.get("locale");
    if (urlLocale) {
      const normalizedLocale = normalizeLocale(urlLocale);
      if (normalizedLocale) {
        return normalizedLocale;
      }
    }

    // Priority 2: Check localStorage for saved preference
    const savedLocale = localStorage.getItem("widget-locale");
    if (savedLocale && isValidLocale(savedLocale)) {
      return savedLocale as SupportedLocale;
    }

    // Priority 3: Check browser language as a hint
    const browserLang = navigator.language;
    if (browserLang.toLowerCase().startsWith("ko")) {
      return "ko-KR";
    }
    if (browserLang.toLowerCase().startsWith("ja")) {
      return "ja-JP";
    }
  }

  // Priority 4: Return config default or fallback to English
  return defaultLocale;
}

// Helper function to normalize locale strings (case-insensitive)
function normalizeLocale(locale: string): SupportedLocale | null {
  const normalized = locale.toLowerCase();

  // Map common variations to our supported formats
  if (normalized === "en" || normalized === "en-us" || normalized === "en_us") {
    return "en-US";
  }
  if (normalized === "ko" || normalized === "ko-kr" || normalized === "ko_kr") {
    return "ko-KR";
  }
  if (normalized === "ja" || normalized === "ja-jp" || normalized === "ja_jp") {
    return "ja-JP";
  }

  // Check exact match after normalization
  const upperCased = locale.toUpperCase().replace("_", "-");
  if (upperCased === "EN-US") return "en-US";
  if (upperCased === "KO-KR") return "ko-KR";
  if (upperCased === "JA-JP") return "ja-JP";

  return null;
}

// Helper function to validate locale
function isValidLocale(locale: string): boolean {
  return SUPPORTED_LOCALES.includes(locale as SupportedLocale);
}

export async function activateLocale(locale: SupportedLocale) {
  // Messages are already loaded in init.ts, just activate the locale
  i18n.activate(locale);
  localStorage.setItem("widget-locale", locale);
}
